import { Link } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import LandingShellSkeleton from "@/components/landing/LandingShellSkeleton";
import PageSkeleton from "@/components/PageSkeleton";
import { useAuthState } from "@/hooks/useAuthState";
import { useModuleSettings } from "@/hooks/useModuleSettings";
import { useLandingSections } from "@/hooks/useLandingSections";
import { useSpotMarkets } from "@/hooks/useSpotTrading";
import { useMultiPriceTicker } from "@/hooks/usePriceFeed";
import { useBranding } from "@/hooks/useBranding";
import { useCountUp } from "@/hooks/useCountUp";
import { useSocialLinks } from "@/hooks/useSocialLinks";
import { getSocialIcon } from "@/lib/socialIcons";
import { supabase } from "@/integrations/supabase/client";
import {
  Shield, Zap, Globe, Headphones, BarChart3, Users,
  ArrowRight, CheckCircle, Star, Lock, TrendingUp, TrendingDown,
  Wallet, Layers, Smartphone, MessageCircle,
  Send, BookOpen, HelpCircle, Mail, Coins, Bell,
} from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import FadeInWhenVisible from "@/components/motion/FadeInWhenVisible";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2 } from "lucide-react";
import StickyNav, { scrollToSection } from "@/components/landing/StickyNav";
import LandingFooter from "@/components/landing/LandingFooter";
import CurrencyIcon from "@/components/CurrencyIcon";
import {
  Table, TableHeader, TableBody, TableHead, TableRow, TableCell,
} from "@/components/ui/table";
import { toast } from "sonner";
import { useLocalTableQuery } from "@/hooks/useTableQuery";
import type { TableQuerySpec } from "@/lib/tableQuery";
import DataToolbar from "@/components/data/DataToolbar";
import { DataTablePagination } from "@/components/DataTablePagination";
import heroScene from "@/assets/hero-crypto-scene.jpg";
import featSpot from "@/assets/feature-spot.png";
import featFutures from "@/assets/feature-futures.png";
import featP2P from "@/assets/feature-p2p.png";
import featWallets from "@/assets/feature-wallets.png";

/* ── Helpers ── */
function fmt(n: number) {
  if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(1)}K`;
  return `$${n.toFixed(2)}`;
}
function fmtPrice(p: number) {
  if (p >= 1000) return p.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  if (p >= 1) return p.toFixed(4);
  return p.toFixed(6);
}
function fmtChange(c: number) {
  const s = c >= 0 ? "+" : "";
  return `${s}${c.toFixed(2)}%`;
}

/* ── Animated stat value ── */
function AnimatedStat({ target, suffix = "", prefix = "", decimals = 0, label }: { target: number; suffix?: string; prefix?: string; decimals?: number; label: string }) {
  const { value, ref } = useCountUp(target, 1500, decimals);
  return (
    <div className="space-y-1">
      <p className="text-4xl md:text-5xl font-extrabold tracking-tight text-foreground">
        <span ref={ref}>{prefix}{decimals > 0 ? value.toFixed(decimals) : value.toLocaleString()}{suffix}</span>
      </p>
      <p className="text-sm text-muted-foreground font-medium">{label}</p>
    </div>
  );
}

/* ── Sample holdings for live portfolio card ── */
const SAMPLE_HOLDINGS = [
  { currency: "BTC", amount: 0.432 },
  { currency: "ETH", amount: 3.21 },
];

/* ── 1. Hero ── */
function HeroSection({ section, tickers }: { section?: any; tickers: Record<string, any> }) {
  const { isAuthed, isInitializing } = useAuthState();
  const { isModuleEnabled } = useModuleSettings();
  const spotEnabled = isModuleEnabled("spot_trading");
  const content = section?.content_json || {};

  const portfolioItems = useMemo(() => {
    return SAMPLE_HOLDINGS.map((h) => {
      const t = tickers[h.currency];
      const price = t?.price || 0;
      const value = price * h.amount;
      return {
        currency: h.currency,
        amount: `${h.amount} ${h.currency}`,
        value: value > 0 ? `$${value.toLocaleString(undefined, { maximumFractionDigits: 0 })}` : "—",
      };
    });
  }, [tickers]);

  const totalValue = useMemo(() => {
    return SAMPLE_HOLDINGS.reduce((sum, h) => {
      const price = tickers[h.currency]?.price || 0;
      return sum + price * h.amount;
    }, 0);
  }, [tickers]);

  const totalChange = useMemo(() => {
    const changes = SAMPLE_HOLDINGS.map((h) => tickers[h.currency]?.change_24h || 0);
    return changes.length ? changes.reduce((a, b) => a + b, 0) / changes.length : 0;
  }, [tickers]);

  return (
    <section className="relative min-h-[calc(100vh-4rem)] flex items-center overflow-hidden bg-background">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <img
          src={heroScene}
          alt=""
          aria-hidden
          className="absolute inset-0 w-full h-full object-cover opacity-30 mix-blend-screen"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background/60 via-background/40 to-background" />
        <div className="absolute -top-40 -right-40 w-[700px] h-[700px] rounded-full bg-primary/15 blur-[120px] animate-blob-float" />
        <div className="absolute -bottom-40 -left-40 w-[600px] h-[600px] rounded-full bg-[hsl(var(--glow-secondary)/0.12)] blur-[120px] animate-blob-float-delayed" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[500px] rounded-full bg-primary/5 blur-[150px]" />
        <div className="absolute inset-0 bg-grid-pattern opacity-20" />
      </div>
      <div className="relative z-10 max-w-7xl mx-auto px-6 py-20 lg:py-28 pt-28 lg:pt-36 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] xl:grid-cols-[1fr_360px] gap-8 lg:gap-10 items-center">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 glass rounded-full px-4 py-1.5 text-sm font-medium text-foreground/90 mb-8 glow-border">
            <span className="w-2 h-2 rounded-full bg-[hsl(var(--success))] animate-pulse" />
            Trusted Digital Asset Platform
          </div>
          <h1 className="text-5xl md:text-7xl font-extrabold leading-[1.05] tracking-tight mb-6 text-foreground">
            {section?.title || "Fast & Secure Platform to Trade Crypto"}
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground mb-10 max-w-2xl leading-relaxed">
            {section?.subtitle || "Trade crypto, access P2P markets, Notes and Futures — all in one ACEX account."}
          </p>
          <div className="flex flex-wrap gap-4 mb-14">
            {isInitializing ? (
              <Button disabled size="lg" variant="glow" className="text-base font-bold px-8 h-12 rounded-full w-56 opacity-60" aria-hidden>
                <span className="opacity-0">Loading</span>
              </Button>
            ) : (
              <>
                <Button asChild size="lg" variant="glow" className="text-base font-bold px-8 h-12 rounded-full">
                  <Link to={isAuthed ? "/app/dashboard" : (content.cta_link || "/signup")}>
                    {isAuthed ? "Go to Dashboard" : (content.cta_text || "Create Account")} <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                {!isAuthed && spotEnabled && (
                  <Button asChild size="lg" variant="outline" className="text-base font-semibold h-12 rounded-full glass">
                    <Link to={content.secondary_cta_link || "/app/markets"}>
                      {content.secondary_cta_text || "Explore Markets"}
                    </Link>
                  </Button>
                )}
              </>
            )}
          </div>
          <div className="flex flex-wrap gap-6 text-sm text-muted-foreground">
            {[
              { icon: Shield, label: "Bank-Grade Security" },
              { icon: CheckCircle, label: "KYC-Ready" },
              { icon: TrendingUp, label: "Fast Withdrawals" },
            ].map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-2">
                <Icon className="h-4 w-4 text-primary/70" />
                <span>{label}</span>
              </div>
            ))}
          </div>
        </div>
        {/* Right: Portfolio card — grid column */}
        <div className="hidden lg:block w-full">
          <div className="glass rounded-2xl p-6 glow-md">
            <div className="flex items-center justify-between mb-4">
              <span className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Portfolio Value</span>
              <span className={`text-xs px-2 py-0.5 rounded-full ${totalChange >= 0 ? "bg-[hsl(var(--success)/0.2)] text-[hsl(var(--success))]" : "bg-destructive/20 text-destructive"}`}>
                {totalChange >= 0 ? "+" : ""}{totalChange.toFixed(1)}%
              </span>
            </div>
            <p className="text-3xl font-bold text-foreground mb-6">
              {totalValue > 0 ? `$${totalValue.toLocaleString(undefined, { maximumFractionDigits: 2 })}` : "—"}
            </p>
            <div className="space-y-3">
              {portfolioItems.map((item) => (
                <div key={item.currency} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <CurrencyIcon currency={item.currency} size={32} />
                    <div>
                      <p className="text-sm font-medium text-foreground">{item.currency}</p>
                      <p className="text-xs text-muted-foreground">{item.amount}</p>
                    </div>
                  </div>
                  <p className="text-sm text-foreground/80">{item.value}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
        </div>
      </div>
    </section>
  );
}

/* ── 2. Trust Strip — real stats ── */
function TrustStrip() {
  const stats = [
    { icon: Users, value: "10K+", label: "Active Users" },
    { icon: BarChart3, value: "$50M+", label: "Volume Traded" },
    { icon: Shield, value: "99.9%", label: "Uptime" },
    { icon: Headphones, value: "24/7", label: "Support" },
  ];
  return (
    <section className="py-10 border-y border-border/30 bg-background">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {stats.map(({ icon: Icon, value, label }) => (
            <div key={label} className="flex items-center gap-3 justify-center">
              <div className="w-10 h-10 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
                <Icon className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-lg font-bold text-foreground">{value}</p>
                <p className="text-xs text-muted-foreground">{label}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── 3. Market Trending Cards ── */
function MarketTrendingSection({ markets, tickers }: { markets: any[]; tickers: Record<string, any> }) {
  const trending = useMemo(() => {
    if (!markets.length) return [];
    return markets
      .map((m: any) => {
        const t = tickers[m.base_asset] || {};
        return { ...m, livePrice: t.price || 0, liveChange: t.change_24h || 0, liveVolume: t.volume_24h || 0 };
      })
      .sort((a: any, b: any) => b.liveVolume - a.liveVolume)
      .slice(0, 6);
  }, [markets, tickers]);

  if (!trending.length) return null;

  return (
    <section className="py-16 md:py-20 bg-background relative overflow-hidden">
      <div className="absolute top-0 right-0 w-[400px] h-[400px] rounded-full bg-primary/5 blur-[120px] pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 glass glow-border rounded-full px-4 py-1.5 text-sm font-semibold text-primary mb-4">Market Trending</div>
          <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-4">Today's Top Performers</h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">Real-time prices from the most traded pairs on the platform.</p>
        </div>
        <div className="flex flex-wrap justify-center items-center gap-4">
          {trending.map((m: any) => {
            const isUp = m.liveChange >= 0;
            return (
              <Link
                key={m.symbol}
                to={`/app/spot/${encodeURIComponent(m.symbol)}`}
                className="glass glow-border rounded-2xl p-5 hover:glow-md hover:-translate-y-1 transition-all duration-300 group"
              >
                <div className="flex items-center gap-2 mb-3">
                  <CurrencyIcon currency={m.base_asset} size={24} />
                  <span className="text-sm font-bold text-foreground">{m.base_asset}<span className="text-muted-foreground font-normal">/{m.quote_asset}</span></span>
                </div>
                <p className="text-lg font-bold text-foreground mb-1">${fmtPrice(m.livePrice)}</p>
                <p className={`text-sm font-semibold ${isUp ? "text-[hsl(var(--success))]" : "text-destructive"}`}>
                  {fmtChange(m.liveChange)}
                </p>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ── 4. Live Exchange Rates Table ── */
const exchangeRatesSpec: TableQuerySpec = {
  searchColumns: ["base_asset", "quote_asset", "symbol"],
  sorts: [
    { key: "liveVolume", label: "24h Volume" },
    { key: "livePrice", label: "Price" },
    { key: "liveChange", label: "24h Change" },
    { key: "base_asset", label: "Pair" },
  ],
  defaultSort: { key: "liveVolume", direction: "desc" },
  searchPlaceholder: "Search pairs...",
};

function ExchangeRatesSection({ markets, tickers }: { markets: any[]; tickers: Record<string, any> }) {
  const allRows = useMemo(() => {
    return markets.map((m: any) => {
      const t = tickers[m.base_asset] || {};
      return { ...m, livePrice: t.price || 0, liveChange: t.change_24h || 0, liveVolume: t.volume_24h || 0 };
    });
  }, [markets, tickers]);

  const table = useLocalTableQuery(allRows, exchangeRatesSpec, { urlKey: "rates", pageSize: 10 });
  const rows = table.rows;

  if (!allRows.length) return null;

  return (
    <section className="py-16 md:py-20 bg-background">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 glass glow-border rounded-full px-4 py-1.5 text-sm font-semibold text-primary mb-4">Live Rates</div>
          <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-4">Crypto Live Exchange Rates</h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">Track real-time prices for the most popular cryptocurrency pairs.</p>
        </div>
        <div className="mb-6 max-w-md mx-auto">
          <DataToolbar query={table.query} resultCount={table.total} bare />
        </div>
        <div className="glass glow-border rounded-2xl overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-border/30">
                <TableHead className="text-muted-foreground">#</TableHead>
                <TableHead className="text-muted-foreground">Pair</TableHead>
                <TableHead className="text-muted-foreground text-right">Price</TableHead>
                <TableHead className="text-muted-foreground text-right">24h Change</TableHead>
                <TableHead className="text-muted-foreground text-right hidden md:table-cell">24h Volume</TableHead>
                <TableHead className="text-right"><span className="sr-only">Action</span></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((m: any, i: number) => {
                const isUp = m.liveChange >= 0;
                return (
                  <TableRow key={m.symbol} className="border-border/20 hover:bg-accent/30 transition-colors">
                    <TableCell className="text-muted-foreground text-sm">{i + 1}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <CurrencyIcon currency={m.base_asset} size={28} />
                        <div>
                          <span className="font-semibold text-foreground">{m.base_asset}</span>
                          <span className="text-muted-foreground">/{m.quote_asset}</span>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-mono font-semibold text-foreground">${fmtPrice(m.livePrice)}</TableCell>
                    <TableCell className={`text-right font-semibold ${isUp ? "text-[hsl(var(--success))]" : "text-destructive"}`}>
                      {fmtChange(m.liveChange)}
                    </TableCell>
                    <TableCell className="text-right text-muted-foreground hidden md:table-cell">{fmt(m.liveVolume)}</TableCell>
                    <TableCell className="text-right">
                      <Button asChild size="sm" variant="glow" className="rounded-full text-xs px-4">
                        <Link to={`/app/spot/${encodeURIComponent(m.symbol)}`}>Trade</Link>
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
        <div className="max-w-md mx-auto mt-4">
          <DataTablePagination page={table.page} totalPages={table.totalPages} onPageChange={table.setPage} showingText={table.showingText} />
        </div>
        <div className="text-center mt-8">
          <Button asChild variant="outline" className="rounded-full glass font-semibold">
            <Link to="/app/markets">View All Markets <ArrowRight className="ml-2 h-4 w-4" /></Link>
          </Button>
        </div>
      </div>
    </section>
  );
}

/* ── 5. Buy/Sell Feature ── */
function BuySellSection() {
  const bullets = [
    "Buy crypto with card or fiat wallet",
    "Sell crypto to your fiat balance instantly",
    "Transparent fees with no hidden charges",
    "Fast execution with real-time pricing",
  ];
  return (
    <section className="py-16 md:py-20 bg-background relative overflow-hidden">
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] rounded-full bg-[hsl(var(--glow-secondary)/0.08)] blur-[120px] pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left — mock card UI */}
          <div className="glass rounded-2xl p-8 glow-md">
            <div className="flex items-center gap-3 mb-6">
              <CurrencyIcon currency="BTC" size={40} />
              <div>
                <p className="text-lg font-bold text-foreground">Buy Bitcoin</p>
                <p className="text-sm text-muted-foreground">BTC/USD</p>
              </div>
            </div>
            <div className="space-y-4">
              <div className="glass rounded-xl p-4 border border-border/30">
                <label className="text-xs text-muted-foreground uppercase tracking-wide">You Pay</label>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-2xl font-bold text-foreground">500.00</span>
                  <span className="text-sm font-semibold text-muted-foreground">USD</span>
                </div>
              </div>
              <div className="glass rounded-xl p-4 border border-border/30">
                <label className="text-xs text-muted-foreground uppercase tracking-wide">You Receive</label>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-2xl font-bold text-[hsl(var(--success))]">0.00812</span>
                  <span className="text-sm font-semibold text-muted-foreground">BTC</span>
                </div>
              </div>
              <Button variant="glow" className="w-full rounded-full h-12 text-base font-bold">Buy Now</Button>
            </div>
          </div>
          {/* Right — text */}
          <div>
            <div className="inline-flex items-center gap-2 glass glow-border rounded-full px-4 py-1.5 text-sm font-semibold text-primary mb-4">Instant Trading</div>
            <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6">Buy & Sell Instantly with ACEX</h2>
            <p className="text-muted-foreground text-lg mb-8 leading-relaxed">
              Purchase cryptocurrency in seconds using your preferred payment method. Simple, secure, and designed for everyone.
            </p>
            <ul className="space-y-4 mb-8">
              {bullets.map((b) => (
                <li key={b} className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                  <span className="text-foreground">{b}</span>
                </li>
              ))}
            </ul>
            <Button asChild size="lg" variant="glow" className="rounded-full font-bold px-8">
              <Link to="/app/trade/buy">Buy Crypto <ArrowRight className="ml-2 h-5 w-5" /></Link>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── 6. Stats Block — animated counters ── */
function StatsSection({ section }: { section?: any }) {
  const items = section?.content_json?.items;
  const useCustom = items && items.length > 0;

  if (useCustom) {
    return (
      <section className="py-12 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-primary/10 via-[hsl(var(--glow-secondary)/0.1)] to-primary/10" />
        <div className="absolute inset-0 bg-grid-pattern opacity-10" />
        <div className="relative z-10 max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {items.map((item: any, i: number) => (
              <div key={i} className="space-y-1">
                <p className="text-4xl md:text-5xl font-extrabold tracking-tight text-foreground">{item.value}</p>
                <p className="text-sm text-muted-foreground font-medium">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-12 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-r from-primary/10 via-[hsl(var(--glow-secondary)/0.1)] to-primary/10" />
      <div className="absolute inset-0 bg-grid-pattern opacity-10" />
      <div className="relative z-10 max-w-6xl mx-auto px-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          <AnimatedStat target={198} suffix="+" label="Supported Pairs" />
          <AnimatedStat target={20} prefix="$" suffix="M+" label="Trade Volume Processed" />
          <AnimatedStat target={350} suffix="+" label="Active Traders Daily" />
          <AnimatedStat target={99.9} suffix="%" decimals={1} label="Uptime" />
        </div>
      </div>
    </section>
  );
}

/* ── 7. Feature Grid — with Futures waitlist ── */
function FeatureGridSection() {
  const [waitlistEmail, setWaitlistEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleWaitlist = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!waitlistEmail.trim()) return;
    setSubmitting(true);
    try {
      const { error } = await supabase.from("waitlist_signups").insert({ email: waitlistEmail.trim(), feature: "futures" } as any);
      if (error && error.code === "23505") {
        toast.info("You're already on the waitlist!");
      } else if (error) {
        throw error;
      } else {
        toast.success("You're on the list! We'll notify you when Futures launches.");
      }
      setSubmitted(true);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const { isModuleEnabled } = useModuleSettings();

  const features = [
    { icon: BarChart3, image: featSpot, title: "Spot Trading", desc: "Trade hundreds of crypto pairs with deep liquidity and real-time order books.", href: "/app/spot", moduleKey: "spot_trading" },
    { icon: Wallet, image: featWallets, title: "Multi-Currency Wallets", desc: "Manage crypto and fiat balances in one secure wallet system.", href: "/app/wallets", moduleKey: "wallets" },
  ].filter((f) => isModuleEnabled(f.moduleKey));

  const showFutures = isModuleEnabled("futures");
  const showP2P = isModuleEnabled("p2p");

  if (!showFutures && !showP2P && features.length === 0) return null;

  return (
    <section className="py-16 md:py-20 bg-background relative overflow-hidden">
      <div className="absolute top-0 left-0 w-[400px] h-[400px] rounded-full bg-primary/5 blur-[120px] pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 glass glow-border rounded-full px-4 py-1.5 text-sm font-semibold text-primary mb-4">Platform Features</div>
          <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-4">Everything You Need to Trade</h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">A complete suite of tools for traders at every level.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Futures — Coming Soon with waitlist */}
          {showFutures && (
          <FadeInWhenVisible delay={0} className="h-full">
          <motion.div whileHover={{ y: -6 }} transition={{ type: "spring", stiffness: 280, damping: 22 }} className="p-7 rounded-2xl glass glow-border relative overflow-hidden h-full">
            <div className="absolute top-4 right-4">
              <span className="text-[10px] font-bold uppercase tracking-wider bg-primary/15 text-primary px-2.5 py-1 rounded-full border border-primary/20">Coming Soon</span>
            </div>
            <div className="h-28 mb-4 flex items-center justify-center">
              <img src={featFutures} alt="" aria-hidden loading="lazy" className="h-28 w-28 object-contain drop-shadow-[0_0_20px_hsl(var(--primary)/0.4)]" />
            </div>
            <h3 className="text-lg font-bold text-foreground mb-2">Futures Trading</h3>
            <p className="text-muted-foreground text-sm leading-relaxed mb-4">Go long or short with up to 125x leverage on perpetual contracts.</p>
            {submitted ? (
              <div className="flex items-center gap-2 text-sm text-[hsl(var(--success))]">
                <CheckCircle className="h-4 w-4" />
                <span>You're on the waitlist!</span>
              </div>
            ) : (
              <form onSubmit={handleWaitlist} className="flex gap-2">
                <Input
                  type="email"
                  required
                  placeholder="Enter email"
                  value={waitlistEmail}
                  onChange={(e) => setWaitlistEmail(e.target.value)}
                  className="h-9 text-sm rounded-full"
                />
                <Button type="submit" size="sm" variant="glow" className="rounded-full shrink-0 px-4" disabled={submitting} aria-label="Subscribe to notifications">
                  {submitting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Bell className="h-3 w-3" />}
                </Button>
              </form>
            )}
          </motion.div>
          </FadeInWhenVisible>
          )}

          {/* Advanced P2P — Coming Soon */}
          {showP2P && (
          <FadeInWhenVisible delay={0.05} className="h-full">
          <motion.div whileHover={{ y: -6 }} transition={{ type: "spring", stiffness: 280, damping: 22 }} className="p-7 rounded-2xl glass glow-border relative overflow-hidden h-full">
            <div className="absolute top-4 right-4">
              <span className="text-[10px] font-bold uppercase tracking-wider bg-primary/15 text-primary px-2.5 py-1 rounded-full border border-primary/20">Coming Soon</span>
            </div>
            <div className="h-28 mb-4 flex items-center justify-center">
              <img src={featP2P} alt="" aria-hidden loading="lazy" className="h-28 w-28 object-contain drop-shadow-[0_0_20px_hsl(var(--primary)/0.4)]" />
            </div>
            <h3 className="text-lg font-bold text-foreground mb-2">Advanced P2P</h3>
            <p className="text-muted-foreground text-sm leading-relaxed">Buy and sell crypto directly with other users using your preferred payment method with escrow protection.</p>
          </motion.div>
          </FadeInWhenVisible>
          )}

          {features.map(({ image, title, desc, href }, i) => (
            <FadeInWhenVisible key={title} delay={0.1 + i * 0.05} className="h-full">
              <motion.div whileHover={{ y: -6 }} transition={{ type: "spring", stiffness: 280, damping: 22 }} className="h-full">
                <Link to={href} className="group p-7 rounded-2xl glass glow-border hover:glow-md transition-all duration-300 flex flex-col h-full">
                  <div className="h-28 mb-4 flex items-center justify-center">
                    <img src={image} alt="" aria-hidden loading="lazy" className="h-28 w-28 object-contain drop-shadow-[0_0_20px_hsl(var(--primary)/0.4)] group-hover:scale-110 transition-transform duration-300" />
                  </div>
                  <h3 className="text-lg font-bold text-foreground mb-2">{title}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed mb-3 flex-1">{desc}</p>
                  <span className="text-primary text-sm font-semibold group-hover:underline">Learn more →</span>
                </Link>
              </motion.div>
            </FadeInWhenVisible>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── 8. Mobile App Promo ── */
function MobileAppSection() {
  return (
    <section className="py-16 md:py-20 bg-background relative overflow-hidden">
      <div className="absolute top-1/2 right-0 w-[500px] h-[500px] rounded-full bg-primary/8 blur-[120px] pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Phone mockup */}
          <div className="flex justify-center">
            <div className="w-64 h-[500px] rounded-[2.5rem] border-4 border-border/40 bg-card overflow-hidden shadow-2xl relative">
              <div className="absolute top-0 inset-x-0 h-8 bg-card flex items-center justify-center">
                <div className="w-20 h-5 rounded-full bg-muted" />
              </div>
              <div className="pt-12 px-4 space-y-3">
                <div className="glass rounded-xl p-3">
                  <p className="text-xs text-muted-foreground">Total Balance</p>
                  <p className="text-xl font-bold text-foreground">$12,458.30</p>
                </div>
                {["BTC", "ETH", "SOL"].map((c) => (
                  <div key={c} className="flex items-center gap-3 glass rounded-xl p-3">
                    <CurrencyIcon currency={c} size={24} />
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-foreground">{c}</p>
                      <p className="text-xs text-muted-foreground">+2.4%</p>
                    </div>
                    <p className="text-sm font-mono text-foreground">$4,152</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
          {/* Text */}
          <div>
            <div className="inline-flex items-center gap-2 glass glow-border rounded-full px-4 py-1.5 text-sm font-semibold text-primary mb-4">
              <Smartphone className="h-4 w-4" /> Mobile Ready
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6">Build Your Crypto Portfolio Anywhere</h2>
            <p className="text-muted-foreground text-lg mb-8 leading-relaxed">
              Install ACEX as a Progressive Web App on any device. Trade, track, and manage your portfolio on the go.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button asChild size="lg" variant="glow" className="rounded-full font-bold px-8">
                <Link to="/app/dashboard">Open ACEX App <ArrowRight className="ml-2 h-5 w-5" /></Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── 9. Testimonials ── */
function TestimonialsSection({ section }: { section?: any }) {
  const fallback = [
    { name: "Alex K.", initials: "AK", role: "Day Trader", quote: "ACEX has the fastest execution I've seen. The interface is clean and the fees are very competitive." },
    { name: "Maria S.", initials: "MS", role: "Crypto Investor", quote: "Managing my crypto, P2P activity and futures positions in one place saves me so much time." },
    { name: "James T.", initials: "JT", role: "P2P Merchant", quote: "The P2P marketplace is well-designed. I've completed hundreds of trades with zero disputes." },
  ];
  const items = section?.content_json?.items || fallback;
  return (
    <section className="py-16 md:py-20 bg-background">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 glass glow-border rounded-full px-4 py-1.5 text-sm font-semibold text-primary mb-4">Testimonials</div>
          <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-4">{section?.title || "What Our Traders Say"}</h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">{section?.subtitle || "Hear from the community that trusts ACEX."}</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {items.map((item: any, i: number) => (
            <div key={i} className="p-7 rounded-2xl glass glow-border hover:glow-md transition-all duration-300">
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, j) => (
                  <Star key={j} className="h-4 w-4 fill-primary text-primary" />
                ))}
              </div>
              <p className="text-muted-foreground text-sm leading-relaxed mb-6 italic">"{item.quote}"</p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/15 border border-primary/20 flex items-center justify-center text-sm font-bold text-primary">{item.initials}</div>
                <div>
                  <p className="text-sm font-bold text-foreground">{item.name}</p>
                  <p className="text-xs text-muted-foreground">{item.role}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── 10. Community ── */
function CommunitySection() {
  const { data: socialLinks = [] } = useSocialLinks();
  return (
    <section className="py-16 md:py-20 bg-background">
      <div className="max-w-5xl mx-auto px-6 text-center">
        <div className="inline-flex items-center gap-2 glass glow-border rounded-full px-4 py-1.5 text-sm font-semibold text-primary mb-4">Community</div>
        <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-4">Join the Worldwide Community</h2>
        <p className="text-muted-foreground text-lg mb-12">Connect with thousands of traders and stay up to date.</p>
        <div className="flex flex-wrap justify-center items-center gap-4">
          {socialLinks.map((link: any) => {
            const { icon: Icon, color } = getSocialIcon(link.icon_key, link.platform);
            return (
              <a key={link.id} href={link.url} target="_blank" rel="noopener noreferrer" className="glass glow-border rounded-2xl p-6 hover:glow-md hover:-translate-y-1 transition-all duration-300 group w-[calc(50%-0.5rem)] md:w-[calc(25%-0.75rem)]">
                <div className={`w-14 h-14 rounded-xl ${color} flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform`}>
                  <Icon className="h-7 w-7" />
                </div>
                <p className="font-bold text-foreground">{link.label}</p>
              </a>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ── 10b. Leadership ── */
function LeadershipSection({ section }: { section?: any }) {
  const fallback = [
    { initials: "AK", name: "Alex Kovalev", role: "CEO", bio: "Platform vision & strategy" },
    { initials: "SM", name: "Sarah Mitchell", role: "CTO", bio: "Engineering & infrastructure" },
    { initials: "JR", name: "James Rivera", role: "COO", bio: "Operations & compliance" },
    { initials: "LN", name: "Lisa Nguyen", role: "CPO", bio: "Product & user experience" },
  ];
  const leaders = (section?.content_json?.items || fallback).map((l: any, i: number) => ({
    ...l,
    gradient: ["from-primary to-accent", "from-accent to-primary", "from-primary/80 to-accent/80", "from-accent/80 to-primary/80"][i % 4],
  }));
  return (
    <section className="py-16 md:py-20 bg-background">
      <div className="max-w-5xl mx-auto px-6 text-center">
        <div className="inline-flex items-center gap-2 glass glow-border rounded-full px-4 py-1.5 text-sm font-semibold text-primary mb-4">Leadership</div>
        <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-4">Meet the Team</h2>
        <p className="text-muted-foreground text-lg mb-12">The people building the future of digital asset trading.</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {leaders.map(({ initials, name, role, bio, gradient }) => (
            <div key={name} className="glass glow-border rounded-2xl p-6 hover:glow-md hover:-translate-y-1 transition-all duration-300 group text-center">
              <div className={`w-20 h-20 rounded-full bg-gradient-to-br ${gradient} flex items-center justify-center mx-auto mb-4 text-primary-foreground text-xl font-bold group-hover:scale-110 transition-transform`}>
                {initials}
              </div>
              <p className="font-bold text-foreground text-lg">{name}</p>
              <p className="text-sm font-semibold text-primary mb-1">{role}</p>
              <p className="text-sm text-muted-foreground">{bio}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── 11. Blog Preview ── */
function BlogSection({ section }: { section?: any }) {
  const fallback = [
    { title: "Understanding Crypto Futures Trading", excerpt: "A beginner's guide to leveraged trading on perpetual contracts.", category: "Education", date: "Feb 28, 2026" },
    { title: "How P2P Trading Works on ACEX", excerpt: "Buy and sell crypto directly with other users safely and securely.", category: "Platform", date: "Feb 25, 2026" },
    { title: "Understanding Digital Asset Notes", excerpt: "Learn how Notes can support a structured digital asset strategy.", category: "Education", date: "Feb 22, 2026" },
  ];
  const posts = section?.content_json?.items || fallback;
  return (
    <section className="py-16 md:py-20 bg-background">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 glass glow-border rounded-full px-4 py-1.5 text-sm font-semibold text-primary mb-4">
            <BookOpen className="h-4 w-4" /> Blog
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-4">Latest from ACEX</h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">Stay informed with market insights and platform updates.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {posts.map((p) => (
            <div key={p.title} className="glass glow-border rounded-2xl overflow-hidden hover:glow-md hover:-translate-y-1 transition-all duration-300 group">
              <div className="h-40 bg-gradient-to-br from-primary/20 via-[hsl(var(--glow-secondary)/0.15)] to-primary/10 flex items-center justify-center">
                <BookOpen className="h-10 w-10 text-primary/40" />
              </div>
              <div className="p-6">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xs font-semibold text-primary bg-primary/10 px-2 py-0.5 rounded-full">{p.category}</span>
                  <span className="text-xs text-muted-foreground">{p.date}</span>
                </div>
                <h3 className="text-lg font-bold text-foreground mb-2 group-hover:text-primary transition-colors">{p.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{p.excerpt}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── 12. Support Strip ── */
function SupportStrip() {
  const blocks = [
    { icon: Headphones, title: "Support Center", desc: "Get help from our support team 24/7.", href: "/app/support" },
    { icon: HelpCircle, title: "FAQs", desc: "Find answers to commonly asked questions.", href: "/app/support" },
    { icon: Mail, title: "Contact Us", desc: "Reach out via email or live chat.", href: "/app/support" },
  ];
  return (
    <section className="py-12 bg-background border-y border-border/30">
      <div className="max-w-5xl mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {blocks.map(({ icon: Icon, title, desc, href }) => (
            <Link key={title} to={href} className="glass glow-border rounded-2xl p-6 text-center hover:glow-md hover:-translate-y-1 transition-all duration-300 group">
              <div className="w-12 h-12 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center mx-auto mb-4 group-hover:bg-primary/20 transition-colors">
                <Icon className="h-6 w-6 text-primary" />
              </div>
              <h3 className="font-bold text-foreground mb-1">{title}</h3>
              <p className="text-sm text-muted-foreground">{desc}</p>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── 13. Final CTA Banner ── */
function FinalCTA() {
  const { isAuthed, isInitializing } = useAuthState();
  return (
    <section className="py-16 md:py-20 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-[hsl(var(--glow-secondary)/0.1)] pointer-events-none" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-primary/10 blur-[150px] pointer-events-none" />
      <div className="relative z-10 max-w-3xl mx-auto px-6 text-center">
        <h2 className="text-4xl md:text-5xl font-extrabold text-foreground mb-5 leading-tight">Start Trading with ACEX Today</h2>
        <p className="text-muted-foreground text-lg mb-10 leading-relaxed">Join thousands of traders using the most complete digital asset platform.</p>
        <div className="flex flex-wrap justify-center gap-4">
          {isInitializing ? (
            <Button disabled size="lg" variant="glow" className="text-base font-bold px-10 h-12 rounded-full w-56 opacity-60" aria-hidden>
              <span className="opacity-0">Loading</span>
            </Button>
          ) : (
            <>
              <Button asChild size="lg" variant="glow" className="text-base font-bold px-10 h-12 rounded-full">
                <Link to={isAuthed ? "/app/dashboard" : "/signup"}>{isAuthed ? "Go to Dashboard" : "Get Started"} <ArrowRight className="ml-2 h-5 w-5" /></Link>
              </Button>
              {!isAuthed && (
                <Button asChild size="lg" variant="outline" className="text-base font-semibold px-8 h-12 rounded-full glass">
                  <Link to="/login">Sign In</Link>
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </section>
  );
}

/* ── Main Page ── */
export default function LandingPage() {
  const { sections, loading, getSection } = useLandingSections();
  const { isModuleEnabled, isLoading: modulesLoading } = useModuleSettings();
  const spotEnabled = isModuleEnabled("spot_trading");
  const walletsEnabled = isModuleEnabled("wallets");
  const { data: spotMarkets } = useSpotMarkets();
  const baseAssets = useMemo(() => (spotMarkets || []).map((m: any) => m.base_asset as string), [spotMarkets]);
  const { data: tickers } = useMultiPriceTicker(baseAssets.length ? baseAssets : undefined);

  // Max loading duration — never block longer than 6 seconds
  const [timedOut, setTimedOut] = useState(false);
  useEffect(() => {
    if (!loading) return;
    const t = setTimeout(() => setTimedOut(true), 6000);
    return () => clearTimeout(t);
  }, [loading]);

  useEffect(() => {
    if (!loading && window.location.hash) {
      const id = window.location.hash.replace("#", "");
      setTimeout(() => scrollToSection(id), 150);
    }
  }, [loading]);

  if ((loading || modulesLoading) && !timedOut) {
    return <LandingShellSkeleton />;
  }

  const heroSection = getSection("hero");
  const statsSection = getSection("stats");
  const testimonialsSection = getSection("testimonials");
  const markets = spotMarkets || [];
  const tickerData = tickers || {};

  return (
    <div className="min-h-screen bg-background">
      <StickyNav />
      <main>
        {heroSection?.visible !== false && <HeroSection section={heroSection} tickers={tickerData} />}
        {getSection("trust_strip")?.visible !== false && <TrustStrip />}
        {spotEnabled && getSection("market_trending")?.visible !== false && <MarketTrendingSection markets={markets} tickers={tickerData} />}
        {spotEnabled && getSection("exchange_rates")?.visible !== false && <ExchangeRatesSection markets={markets} tickers={tickerData} />}
        {walletsEnabled && getSection("buy_sell")?.visible !== false && <BuySellSection />}
        {statsSection?.visible !== false && <StatsSection section={statsSection} />}
        {getSection("feature_grid")?.visible !== false && <FeatureGridSection />}
        {getSection("mobile_app")?.visible !== false && <MobileAppSection />}
        {testimonialsSection?.visible !== false && <TestimonialsSection section={testimonialsSection} />}
        {getSection("community")?.visible !== false && <CommunitySection />}
        {getSection("leadership")?.visible !== false && <LeadershipSection section={getSection("leadership")} />}
        {getSection("blog")?.visible !== false && <BlogSection section={getSection("blog")} />}
        {getSection("support_strip")?.visible !== false && <SupportStrip />}
        {getSection("final_cta")?.visible !== false && <FinalCTA />}
      </main>
      <LandingFooter />
    </div>
  );
}
